// Phase 7 R1+R2+R3+R4 — purchase order detail page (/purchasing/:id).
// R1: header + lines, Cancel on draft.
// R2: Submit on draft (mirrors linked parts to 'ordered').
// R3: Receive Parts on submitted/partially_received + Receipts history card
//     + per-line "Received X / Y" counter.
// R4: Return Parts on received/partially_received/partially_returned +
//     Returns history card + per-line "Returned A / X" counter. PO header
//     never auto-closes — terminal R4 state is partially_returned.
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePurchaseOrder } from '@/hooks/purchasing/usePurchaseOrder';
import { useCancelPurchaseOrder } from '@/hooks/purchasing/useCancelPurchaseOrder';
import { useSubmitPurchaseOrder } from '@/hooks/purchasing/useSubmitPurchaseOrder';
import { usePurchaseOrderReceipts } from '@/hooks/purchasing/usePurchaseOrderReceipts';
import { usePurchaseOrderReturns } from '@/hooks/purchasing/usePurchaseOrderReturns';
import { useUserRole } from '@/hooks/useUserRole';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { PurchaseOrderStatusBadge } from './PurchaseOrderStatusBadge';
import { ReceivePartsDialog } from './ReceivePartsDialog';
import { ReturnPartsDialog } from './ReturnPartsDialog';
import { ProcurementTimelineCard } from './ProcurementTimelineCard';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Ban, Send, PackageCheck, Undo2, ChevronDown } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleDateString() : '—';

const fmtDateTime = (s: string | null) =>
  s ? new Date(s).toLocaleString() : '—';

const fmtMoney = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

export default function PurchaseOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = usePurchaseOrder(id);
  const { data: receipts } = usePurchaseOrderReceipts(id);
  const { data: returns } = usePurchaseOrderReturns(id);
  const { role } = useUserRole();
  const cancelMut = useCancelPurchaseOrder();
  const submitMut = useSubmitPurchaseOrder();
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [expandedReceipt, setExpandedReceipt] = useState<string | null>(null);
  const [expandedReturn, setExpandedReturn] = useState<string | null>(null);

  const canWrite = role === 'OWNER' || role === 'MANAGER' || role === 'SERVICE_WRITER';
  const isDraft = data?.header.status === 'draft';
  const canCancel = canWrite && isDraft;
  const canSubmit = canWrite && isDraft;
  const canReceive =
    canWrite &&
    (data?.header.status === 'submitted' || data?.header.status === 'partially_received');
  const canReturn =
    canWrite &&
    (data?.header.status === 'received' ||
      data?.header.status === 'partially_received' ||
      data?.header.status === 'partially_returned');

  const handleCancel = async () => {
    if (!id) return;
    if (!window.confirm('Cancel this draft PO? This cannot be undone.')) return;
    try {
      await cancelMut.mutateAsync(id);
      toast({ title: 'Purchase order canceled' });
    } catch (e: any) {
      toast({ title: 'Cancel failed', description: e?.message, variant: 'destructive' });
    }
  };

  const handleSubmit = async () => {
    if (!id || !data) return;
    setSubmitConfirmOpen(false);
    try {
      await submitMut.mutateAsync({ shopId: data.header.shop_id, purchaseOrderId: id });
      toast({
        title: 'Purchase order submitted',
        description: 'Linked ticket parts have been marked as ordered.',
      });
    } catch (e: any) {
      toast({ title: 'Submit failed', description: e?.message, variant: 'destructive' });
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="container mx-auto p-6">
        <Button variant="ghost" onClick={() => navigate('/purchasing')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div className="mt-6 rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          Purchase order not found.
        </div>
      </div>
    );
  }

  const { header, lines } = data;
  const linesTotal = lines.reduce(
    (sum, l) => sum + Number(l.quantity_ordered) * Number(l.unit_cost_ordered ?? 0),
    0,
  );

  // Phase 7 R5 — derived summary values (UI-only, not stored).
  const totalOrdered = lines.reduce((s, l) => s + Number(l.quantity_ordered), 0);
  const totalReceived = lines.reduce((s, l) => s + Number(l.cumulative_received ?? 0), 0);
  const totalReturned = lines.reduce((s, l) => s + Number(l.cumulative_returned ?? 0), 0);
  const linesWithReturns = lines.filter((l) => Number(l.cumulative_returned ?? 0) > 0).length;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate('/purchasing')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> All purchase orders
        </Button>
        <div className="flex items-center gap-2">
          {canCancel && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={cancelMut.isPending || submitMut.isPending}
            >
              <Ban className="h-4 w-4 mr-2" /> Cancel PO
            </Button>
          )}
          {canSubmit && (
            <Button
              size="sm"
              onClick={() => setSubmitConfirmOpen(true)}
              disabled={submitMut.isPending || cancelMut.isPending}
            >
              <Send className="h-4 w-4 mr-2" /> Submit PO
            </Button>
          )}
          {canReceive && (
            <Button size="sm" onClick={() => setReceiveOpen(true)}>
              <PackageCheck className="h-4 w-4 mr-2" /> Receive Parts
            </Button>
          )}
          {canReturn && (
            <Button size="sm" variant="outline" onClick={() => setReturnOpen(true)}>
              <Undo2 className="h-4 w-4 mr-2" /> Return Parts
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="font-mono text-xl">{header.po_number}</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                {header.vendor_name || 'No vendor'}
              </p>
            </div>
            <PurchaseOrderStatusBadge status={header.status} />
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 md:grid-cols-4 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Created</div>
            <div>{fmtDate(header.created_at)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Ordered</div>
            <div>{fmtDate(header.ordered_at)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Expected</div>
            <div>{fmtDate(header.expected_at)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Received</div>
            <div>{fmtDate(header.received_at)}</div>
          </div>
          {header.notes && (
            <div className="col-span-2 md:col-span-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Notes</div>
              <div className="whitespace-pre-wrap">{header.notes}</div>
            </div>
          )}
          {/* Phase 7 R5 — procurement summary block (derived, UI-only). */}
          <div className="col-span-2 md:col-span-4 mt-2 rounded-md border bg-muted/20 p-3 text-xs">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-muted-foreground">
              <span><strong className="text-foreground">{lines.length}</strong> line{lines.length === 1 ? '' : 's'}</span>
              <span>Ordered: <strong className="text-foreground tabular-nums">{totalOrdered}</strong> units</span>
              <span>Received: <strong className="text-foreground tabular-nums">{totalReceived}</strong> / {totalOrdered}</span>
              <span>Returned: <strong className="text-foreground tabular-nums">{totalReturned}</strong> units</span>
              <span>Lines with returns: <strong className="text-foreground tabular-nums">{linesWithReturns}</strong></span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Lines ({lines.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {lines.length === 0 ? (
            <div className="rounded-md border border-dashed bg-muted/20 p-6 text-center text-sm text-muted-foreground">
              No lines on this PO.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead>Part #</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Unit Cost</TableHead>
                  <TableHead className="text-right">Core</TableHead>
                  <TableHead className="text-right">Line Total</TableHead>
                  {/* Phase 7 R5 — derived counters */}
                  <TableHead className="text-right">Receivable left</TableHead>
                  <TableHead className="text-right">Returnable</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((l) => {
                  const ordered = Number(l.quantity_ordered);
                  const received = Number(l.cumulative_received ?? 0);
                  const returnedQty = Number(l.cumulative_returned ?? 0);
                  const receivableLeft = Math.max(0, ordered - received);
                  const returnable = Math.max(0, received - returnedQty);
                  return (
                    <TableRow key={l.id}>
                      <TableCell>
                        <div className="font-medium">{l.description}</div>
                        {l.ticket_id && (
                          <div className="text-xs text-muted-foreground">
                            Ticket: <span className="font-mono">{l.ticket_id}</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{l.vendor_part_number || '—'}</TableCell>
                      <TableCell className="text-right tabular-nums">{ordered}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtMoney(l.unit_cost_ordered)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtMoney(l.core_charge_ordered)}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">
                        {fmtMoney(ordered * Number(l.unit_cost_ordered ?? 0))}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {receivableLeft > 0 ? (
                          <span>{receivableLeft}</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {returnable > 0 ? (
                          <span>{returnable}</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <PurchaseOrderStatusBadge status={l.status} />
                          {received > 0 && (
                            <span className="text-[10px] text-muted-foreground tabular-nums">
                              Received {received} / {ordered}
                            </span>
                          )}
                          {returnedQty > 0 && (
                            <span className="text-[10px] text-muted-foreground tabular-nums">
                              Returned {returnedQty} / {received}
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell colSpan={5} className="text-right font-medium">Subtotal</TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">{fmtMoney(linesTotal)}</TableCell>
                  <TableCell colSpan={3} />
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Receipts {receipts && `(${receipts.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!receipts || receipts.length === 0 ? (
            <div className="rounded-md border border-dashed bg-muted/20 p-6 text-center text-sm text-muted-foreground">
              No receipts recorded yet.
            </div>
          ) : (
            <div className="space-y-2">
              {receipts.map((r) => {
                const isOpen = expandedReceipt === r.id;
                return (
                  <Collapsible
                    key={r.id}
                    open={isOpen}
                    onOpenChange={(v) => setExpandedReceipt(v ? r.id : null)}
                  >
                    <CollapsibleTrigger asChild>
                      <button className="w-full flex items-center justify-between rounded-md border bg-card px-3 py-2 text-left text-sm hover:bg-accent/30 transition-colors">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium flex flex-wrap items-baseline gap-x-2">
                            <span>{fmtDateTime(r.received_at)}</span>
                            <span className="text-xs text-muted-foreground">
                              · {r.received_by_name ?? 'Unknown user'}
                            </span>
                            {r.packing_slip_ref && (
                              <span className="text-xs text-muted-foreground">
                                · Slip <span className="font-mono">{r.packing_slip_ref}</span>
                              </span>
                            )}
                            <span className="text-xs text-muted-foreground">
                              · {r.parts_receipt_lines?.length ?? 0} line
                              {(r.parts_receipt_lines?.length ?? 0) === 1 ? '' : 's'}
                            </span>
                          </div>
                          {r.notes && (
                            <div className="text-xs text-muted-foreground">{r.notes}</div>
                          )}
                        </div>
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mt-2 ml-3 rounded-md border bg-muted/10 p-2 space-y-1">
                        {(r.parts_receipt_lines ?? []).map((rl) => {
                          const linkedLine = lines.find((l) => l.id === rl.purchase_order_line_id);
                          return (
                            <div
                              key={rl.id}
                              className="flex items-center justify-between gap-3 rounded px-2 py-1 text-xs"
                            >
                              <div className="min-w-0 flex-1 truncate">
                                {linkedLine?.description ?? rl.purchase_order_line_id}
                              </div>
                              <div className="tabular-nums text-muted-foreground">
                                Qty {Number(rl.quantity_received)}
                                {rl.unit_cost_actual != null && (
                                  <> @ {fmtMoney(rl.unit_cost_actual)}</>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Returns {returns && `(${returns.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!returns || returns.length === 0 ? (
            <div className="rounded-md border border-dashed bg-muted/20 p-6 text-center text-sm text-muted-foreground">
              No returns recorded yet.
            </div>
          ) : (
            <div className="space-y-2">
              {returns.map((r) => {
                const isOpen = expandedReturn === r.id;
                return (
                  <Collapsible
                    key={r.id}
                    open={isOpen}
                    onOpenChange={(v) => setExpandedReturn(v ? r.id : null)}
                  >
                    <CollapsibleTrigger asChild>
                      <button className="w-full flex items-center justify-between rounded-md border bg-card px-3 py-2 text-left text-sm hover:bg-accent/30 transition-colors">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium flex flex-wrap items-baseline gap-x-2">
                            <span>{fmtDateTime(r.created_at)}</span>
                            <span className="text-xs text-muted-foreground">
                              · {r.created_by_name ?? 'Unknown user'}
                            </span>
                            {r.return_reference && (
                              <span className="text-xs text-muted-foreground">
                                · Ref <span className="font-mono">{r.return_reference}</span>
                              </span>
                            )}
                            <span className="text-xs text-muted-foreground">
                              · {r.parts_return_lines?.length ?? 0} line
                              {(r.parts_return_lines?.length ?? 0) === 1 ? '' : 's'}
                            </span>
                          </div>
                          {r.reason && (
                            <div className="text-xs text-muted-foreground">{r.reason}</div>
                          )}
                        </div>
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mt-2 ml-3 rounded-md border bg-muted/10 p-2 space-y-1">
                        {(r.parts_return_lines ?? []).map((rl) => {
                          const linkedLine = lines.find((l) => l.id === rl.purchase_order_line_id);
                          return (
                            <div
                              key={rl.id}
                              className="flex items-center justify-between gap-3 rounded px-2 py-1 text-xs"
                            >
                              <div className="min-w-0 flex-1 truncate">
                                {linkedLine?.description ?? rl.purchase_order_line_id ?? 'Unlinked'}
                                {rl.reason && (
                                  <span className="ml-2 text-muted-foreground">· {rl.reason}</span>
                                )}
                              </div>
                              <div className="tabular-nums text-muted-foreground">
                                Qty {Number(rl.quantity_returned)}
                                {rl.unit_credit != null && (
                                  <> @ {fmtMoney(rl.unit_credit)}</>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Phase 7 R5 — read-only procurement audit timeline (newest first). */}
      <ProcurementTimelineCard poId={id} lines={lines} />

      <AlertDialog open={submitConfirmOpen} onOpenChange={setSubmitConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit purchase order?</AlertDialogTitle>
            <AlertDialogDescription>
              Submitting this PO will mark linked ticket parts as <strong>ordered</strong>.
              This cannot be undone from this screen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmit} disabled={submitMut.isPending}>
              Submit PO
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {canReceive && (
        <ReceivePartsDialog
          open={receiveOpen}
          onOpenChange={setReceiveOpen}
          shopId={header.shop_id}
          purchaseOrderId={header.id}
          poNumber={header.po_number}
          lines={lines}
        />
      )}

      {canReturn && (
        <ReturnPartsDialog
          open={returnOpen}
          onOpenChange={setReturnOpen}
          shopId={header.shop_id}
          purchaseOrderId={header.id}
          poNumber={header.po_number}
          lines={lines}
        />
      )}
    </div>
  );
}
